import { useCallback, useState } from 'react';
import type { RoomMemberDto } from '../../../types/chat';
import { X } from '../../../icons';
import { removeRoomMember } from '../../../api/chat';

/**
 * KickMemberDialog (chat-room-kick — 2026-10-26) — 방장이 참여자를 내보내기 전에 한 번 묻는다.
 *
 * 서버(`DELETE /chat/rooms/:id/members/:userId`)는 2026-09-16 부터 있었고 화면에서 부르는 곳이 없었다.
 *   · 내보내진 사람의 화면은 서버가 그 사람 채널로 보내는 `sse.room_deleted` 를 받아 방을 닫는다
 *     (`/leave` 와 같은 길) — 그래서 여기서는 **내 화면의 멤버 목록**만 고친다.
 *   · 소프트 탈퇴라 다시 초대하면 되살아난다 — 문구에도 그렇게 적는다.
 *   · 이미 나간 사람(`removed: false`)도 목록에서 빼는 것이 맞다(서버 기준으로 이미 멤버가 아니다).
 */
export function kickErrorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
  switch (res?.data?.code) {
    case 'FORBIDDEN': return '방장만 멤버를 내보낼 수 있습니다.';
    case 'CANNOT_KICK_OWNER': return '방장은 내보낼 수 없습니다.';
    case 'CANNOT_KICK_SELF': return '자기 자신은 내보낼 수 없습니다. 「나가기」를 써 주세요.';
    case 'DIRECT_NO_KICK': return '1:1 대화에서는 내보낼 수 없습니다.';
    case 'MEMBER_NOT_FOUND': return '이미 이 방의 멤버가 아닙니다.';
    default: break;
  }
  if (res?.status === 404) return '방을 찾을 수 없습니다.';
  return res?.data?.message ?? '내보내지 못했습니다.';
}

interface KickMemberDialogProps {
  roomId: string;
  target: RoomMemberDto;
  /** 화면에 보이는 이름(내가 지은 이름이 있으면 그것). */
  shownName: string;
  onClose: () => void;
  /** 서버가 받아들인 뒤 — 부모가 목록에서 그 사람을 뺀다. */
  onKicked: (userId: string) => void;
}

export function KickMemberDialog({ roomId, target, shownName, onClose, onKicked }: KickMemberDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await removeRoomMember(roomId, target.userId);
      onKicked(target.userId);
    } catch (err) {
      setError(kickErrorMessage(err));
      setBusy(false);
    }
  }, [roomId, target.userId, onKicked]);

  return (
    <div
      className="chat-detail-scrim"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="chat-detail-dlg is-sm" role="dialog" aria-modal="true" aria-label="내보내기">
        <div className="chat-detail-dlghead">
          <span className="chat-detail-title">내보내기</span>
          <button type="button" className="chat-detail-iconbtn chat-detail-close" aria-label="닫기" onClick={onClose} disabled={busy}>
            <X size={15} />
          </button>
        </div>
        <div className="chat-detail-dlgbody">
          <p className="chat-kick-q">{shownName} 님을 이 대화방에서 내보낼까요?</p>
          <p className="chat-detail-fldnote">
            내보낸 사람은 이 방의 새 메시지를 받지 못합니다. 다시 초대하면 돌아올 수 있습니다.
          </p>
          {error && <p className="chat-detail-err" role="alert">{error}</p>}
          <div className="chat-detail-editfoot">
            <button type="button" className="chat-detail-btn" onClick={onClose} disabled={busy}>취소</button>
            <button type="button" className="chat-detail-btn is-danger" onClick={() => void confirm()} disabled={busy}>
              {busy ? '내보내는 중…' : '내보내기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
